import { ApolloClient } from '@apollo/client';
import type { TypePolicies } from '@apollo/client/cache';
import { InMemoryCache } from '@apollo/client/cache';
import { HttpLink } from '@apollo/client/link/http';
import ApolloLinkTimeout from 'apollo-link-timeout';
import type { Zrm_GetPropertySearchResultResponse } from '../types/GraphqlTypes';

const GRAPHQL_ENDPOINT = '/rental-manager/inbox/graphql';
const TIMEOUT_IN_SECONDS = 15;

const headers: Record<string, string | undefined> = {
  'build_number': process.env.NEXT_PUBLIC_BUILD_NUMBER,
  'x-client-app-name': 'satellite-landlord-web',
};

const timeoutLink = new ApolloLinkTimeout(1000 * TIMEOUT_IN_SECONDS);

const httpLink = new HttpLink({
  uri: __DEV__ ? `${process.env.NEXT_PUBLIC_ZG_API}${GRAPHQL_ENDPOINT}` : GRAPHQL_ENDPOINT, // relative url for prod and qa
  credentials: 'include',
  fetch,
  headers,
});

const timeoutHttpLink = timeoutLink.concat(httpLink);

const typePolicies: TypePolicies = {
  ZRM_Conversation: {
    keyFields: ['conversationId'],
    fields: {
      applicationAlreadySent: {
        keyArgs: false,
        merge(existing?: boolean | null | undefined, incoming?: boolean | null | undefined) {
          // getAllForConversation returns applicationAlreadySent
          // getLatestConversations does not.
          // Without a merge function to deal with this descrepency,
          // ConversationList calls will overwrite existing values for this field
          return typeof existing === 'boolean' ? existing : incoming;
        },
      },
      applicationCompleted: {
        keyArgs: false,
        merge(existing?: boolean | null | undefined, incoming?: boolean | null | undefined) {
          return typeof existing === 'boolean' ? existing : incoming;
        },
      },
      isApplicationsAccepted: {
        keyArgs: false,
        merge(existing?: boolean | null | undefined, incoming?: boolean | null | undefined) {
          return !!existing || !!incoming;
        },
      },
      conversation: {
        keyArgs: false,
        /**
         * Merge scenarios
         * 1) ConversationList poll finds a new message (incoming comes second)
         * 2) MessageList loads messages on initial fetch (incoming comes first)
         * 3) MessageList poll finds a new message (incoming comes second)
         * 4) MessageList pagination (incoming comes first)
         */
        merge(existing: any, incoming: any, { variables }) {
          if (existing) {
            if (variables?.conversationId) {
              if (variables?.paginationStartIndex) {
                return mergeMessages([...incoming, ...existing]);
              }
            }

            // If a single incoming message already exists don't merge it with existing messages
            // to prevent out of order messages
            if (incoming.length === 1) {
              const existingMessageRefs = existing.flatMap((existingItem: any) => existingItem['__ref']);

              if (existingMessageRefs.includes(incoming[0]['__ref'])) {
                return [...existing];
              }
            }

            return mergeMessages([...existing, ...incoming]);
          }

          return [...incoming];
        },
      },
    },
  },
  ZRM_Message: {
    keyFields: ['conversationId', 'messageId'],
    fields: {
      message: {
        keyArgs: false,
        merge(existing?: string | null | undefined, incoming?: string | null | undefined) {
          return incoming || existing || '';
        },
      },
      messagePreview: {
        keyArgs: false,
        merge(existing?: string | null | undefined, incoming?: string | null | undefined) {
          return incoming || existing || '';
        },
      },
    },
  },
  ZRM_Note: {
    keyFields: ['conversationId'],
  },
  ZRM_Attachment: {
    keyFields: ['zDocId'],
    fields: {
      previewUrl: {
        keyArgs: false,
        merge(existing?: string | null | undefined, incoming?: string | null | undefined) {
          return existing || incoming;
        },
      },
    },
  },
  SavedReply: {
    keyFields: ['replyIndex', 'reply', 'title'],
  },
  ZRM_PropertyListing: {
    keyFields: ['listingAlias'],
  },
  ZRM_LeadManagementLead: {
    keyFields: ['conversationId'],
  },
  Query: {
    fields: {
      ZRM_getPropertySearchResult: {
        keyArgs: ['searchText'],
        merge(existing: Zrm_GetPropertySearchResultResponse, incoming: Zrm_GetPropertySearchResultResponse) {
          if (!existing) {
            return incoming;
          }

          return {
            ...incoming,
            listings: [...existing.listings, ...incoming.listings],
          };
        },
      },
    },
  },
};

// All real-time updates now flow through `@zg-rentals/web-socket-react`
// (see `_app.tsx`, `useRealtimeUpdates`, and `useMessageListRealtimeUpdates`).
// Apollo is HTTP-only — no GraphQL subscriptions remain in SLW.
const apolloClient = new ApolloClient({
  link: timeoutHttpLink,
  cache: new InMemoryCache({
    typePolicies,
  }),
});

/**
 *
 * @param arr of apollo normalized message references, possibly containing duplicates
 * @returns an array of deduped messages
 * @description when merging a conversation (an array of messages), we want to merge from the bottom
 * so that recent messages persist at the bottom. This function dedupes from the end of the array vs the front
 */
function mergeMessages(arr: any) {
  const newArr: Array<any> = [];
  const keySet: Set<string> = new Set();

  for (let i = arr.length - 1; i >= 0; i--) {
    const newKey = arr[i]['__ref'];
    if (!keySet.has(newKey)) {
      newArr.unshift(arr[i]);
      keySet.add(newKey);
    }
  }
  return newArr;
}


export default apolloClient;
